import React, { useEffect, useState } from 'react';
import {
  Form,
  Select,
  Skeleton,
  Row,
  Col,
  Button,
  Input,
  Table,
  Popconfirm,
} from 'antd';
import { useDispatch } from 'react-redux';
import get from 'lodash/get';
import map from 'lodash/map';
import remove from 'lodash/remove';
import find from 'lodash/find';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import assign from 'lodash/assign';
import { DeleteTwoTone, ArrowLeftOutlined } from '@ant-design/icons';
import { locationManagement } from '../../../redux/actions/locationAction';
import { productMangement } from '../../../redux/actions/productAction';
import AddGovermentCompliance from '../../../components/product/addGovermentCompliance';
import AddProductSize from '../../../components/product/addProductSize';
import AddProductShape from '../../../components/product/addProductShape';
import AddProductPackaging from '../../../components/product/addProductPackaging';
import AddProductProperty from '../../../components/product/addProductProperty';

const { Option } = Select;

function AdditionalProductInformationForm(props) {
  const { onSubmitPayload, onMove, initialValues, edit, companyId } = props;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [productProperty, setProductProperty] = useState(
    initialValues ? get(initialValues, 'product_property') : []
  );

  const dispatch = useDispatch();
  const [dropdownData, setDropDownData] = useState([]);

  const onFinish = (values) => {
    const meta = {
      country: find(get(dropdownData, '0'), ['id', get(values, 'country_id')]),
      shelf: find(get(dropdownData, '1'), ['id', get(values, 'shelf_life_id')]),
      shape: find(get(dropdownData, '2'), [
        'id',
        get(values, 'product_shape_id'),
      ]),
      size: find(get(dropdownData, '3'), [
        'id',
        get(values, 'product_size_id'),
      ]),
      packaging: find(get(dropdownData, '4'), [
        'id',
        get(values, 'packaging_id'),
      ]),
      compliance: filter(get(dropdownData, '5'), (comp) =>
        includes(get(values, 'product_government_compliance'), get(comp, 'id'))
      ),
      product_property: map(get(values, 'product_property'), (property, i) =>
        assign(property, {
          product_property_type_id: get(productProperty, `${i}.id`),
          name: get(productProperty, `${i}.name`),
        })
      ),
    };

    onSubmitPayload(
      2,
      assign(values, {
        meta,
        product_property: map(get(values, 'product_property'), (property, i) =>
          assign(property, {
            product_property_type_id: get(productProperty, `${i}.id`),
            name: get(productProperty, `${i}.name`),
          })
        ),
      })
    );
  };

  const refreshDropDown = async () => {
    setLoading(true);
    try {
      const responseArray = await Promise.all([
        dispatch(locationManagement('list.countries')),
        dispatch(productMangement('list.shelf_life')),
        dispatch(
          productMangement('list.product_shapes', {
            company_id: companyId,
          })
        ),
        dispatch(
          productMangement('list.product_sizes', {
            company_id: companyId,
          })
        ),
        dispatch(
          productMangement('list.packaging', {
            company_id: companyId,
          })
        ),
        dispatch(
          productMangement('list.government_compliances', {
            company_id: companyId,
          })
        ),
      ]);

      setLoading(false);
      setDropDownData(map(responseArray, 'data'));
    } catch (e) {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshDropDown();
  }, []);

  const onRemoveProductProperty = (index) => {
    const formProductProperty = form.getFieldValue('product_property');
    remove(productProperty, (i, k) => k === index);
    remove(formProductProperty, (i, k) => k === index);
    form.setFieldValue({
      product_property: [...formProductProperty],
    });
    setProductProperty([...productProperty]);
  };

  return (
    <Skeleton loading={loading}>
      <Form
        name="basic"
        form={form}
        layout="vertical"
        initialValues={initialValues || {}}
        onFinish={onFinish}
        autoComplete="off"
      >
        <Row gutter={32}>
          <Col xl={6}>
            <Form.Item
              label="Country of origin"
              name="country_id"
              className="form-item"
              rules={[
                {
                  required: true,
                  message: 'Please select a country!',
                },
              ]}
            >
              <Select
                disabled={edit}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                showSearch
                size="large"
                placeholder="Eg.India"
              >
                {map(get(dropdownData, '0', []), (cat) => (
                  <Option
                    value={get(cat, 'id')}
                    label={get(cat, 'name')}
                    key={get(cat, 'id')}
                  >
                    {get(cat, 'name')}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xl={6}>
            <Form.Item
              label="Shelf life"
              name="shelf_life_id"
              className="form-item"
              rules={[
                {
                  required: true,
                  message: 'Please select a shelf life!',
                },
              ]}
            >
              <Select size="large" placeholder="Eg. 6 Month">
                {map(get(dropdownData, '1', []), (cat) => (
                  <Option value={get(cat, 'id')} key={get(cat, 'id')}>
                    {get(cat, 'name')}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xl={6}>
            <Form.Item
              label="Color of Product"
              name="color"
              className="form-item"
            >
              <Input size="large" placeholder="Eg.Green" />
            </Form.Item>
          </Col>
          <Col xl={6}>
            <Form.Item
              label="Shape of product life"
              name="product_shape_id"
              className="form-item"
              extra={
                <AddProductShape
                  refreshData={refreshDropDown}
                  companyId={companyId}
                />
              }
            >
              <Select size="large" placeholder="Select shape of product">
                {map(get(dropdownData, '2', []), (cat) => (
                  <Option value={get(cat, 'id')} key={get(cat, 'id')}>
                    {get(cat, 'name')}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={32} className="mt-40">
          <Col xl={6}>
            <Form.Item
              label="Flavour/Variant"
              name="flavour"
              className="form-item"
            >
              <Input size="large" placeholder="Eg. Vanilla" disabled={edit} />
            </Form.Item>
          </Col>
          <Col xl={6}>
            <Form.Item
              label="Weight/Volume"
              name="weight_vol"
              rules={[
                {
                  required: true,
                  message: 'Please input Weight/Volume!',
                },
                {
                  pattern: new RegExp(/^(\d*\.)?\d+\s*[a-zA-Z]+$/),
                  message:
                    'Enter Weight/Volume in correct format Eg. 10g, 10.99 mg',
                },
              ]}
              className="form-item"
            >
              <Input size="large" placeholder="Eg. 0.6kg/m3" />
            </Form.Item>
          </Col>
          <Col xl={6}>
            <Form.Item
              label="Size of product"
              name="product_size_id"
              className="form-item"
              extra={
                <AddProductSize
                  refreshData={refreshDropDown}
                  companyId={companyId}
                />
              }
            >
              <Select size="large" placeholder="Select size of product">
                {map(get(dropdownData, '3', []), (cat) => (
                  <Option value={get(cat, 'id')} key={get(cat, 'id')}>
                    {get(cat, 'name')}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xl={6}>
            <Form.Item
              label="Packaging"
              name="packaging_id"
              className="form-item"
              extra={
                <AddProductPackaging
                  refreshData={refreshDropDown}
                  companyId={companyId}
                />
              }
            >
              <Select size="large" placeholder="Select Packaging">
                {map(get(dropdownData, '4', []), (cat) => (
                  <Option value={get(cat, 'id')} key={get(cat, 'id')}>
                    {get(cat, 'name')}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={32} className="mt-40">
          <Col xl={8}>
            <Form.Item
              label="Goverment Complaince"
              name="product_government_compliance"
              className="form-item"
              extra={
                <AddGovermentCompliance
                  refreshData={refreshDropDown}
                  companyId={companyId}
                />
              }
            >
              <Select
                mode="multiple"
                size="large"
                placeholder="Select Goverment Compliance"
              >
                {map(get(dropdownData, '5', []), (cat) => (
                  <Option value={get(cat, 'id')} key={get(cat, 'id')}>
                    {get(cat, 'name')}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xl={16}>
            <p className="normal-heading ">Product Property </p>
            <Table
              size="small"
              bordered
              pagination={false}
              columns={[
                {
                  width: '30%',
                  align: 'center',
                  title: 'Property',
                  key: 'property',
                  dataIndex: 'name',
                },
                {
                  width: '30%',
                  align: 'center',
                  title: 'Value',
                  key: 'value',
                  render: (t, record, index) => (
                    <Form.Item
                      label=""
                      name={['product_property', index, 'value']}
                      className="form-item"
                      rules={[
                        {
                          required: true,
                          message: 'Please input value',
                        },
                      ]}
                    >
                      <Input placeholder="Enter value" size="large" />
                    </Form.Item>
                  ),
                },
                {
                  title: 'Action',
                  key: 'action',
                  render: (t, record, index) => (
                    <Popconfirm
                      title="Are you sure to remove this nutrition?"
                      onConfirm={() => {
                        onRemoveProductProperty(index);
                      }}
                      okText="Yes"
                      cancelText="No"
                    >
                      <DeleteTwoTone
                        className="table-icon"
                        twoToneColor="#009EA8"
                      />
                    </Popconfirm>
                  ),
                },
              ]}
              dataSource={productProperty}
            />
            <AddProductProperty
              selectedProductProperty={productProperty}
              onSelectProductProperty={(newProductProperty) => {
                setProductProperty([...productProperty, newProductProperty]);
              }}
              companyId={companyId}
            />
          </Col>
        </Row>
        <Row justify="center" className="mt-60">
          <Col xl={8}>
            <Button
              className="default-primary-btn"
              type="primary"
              icon={<ArrowLeftOutlined />}
              onClick={() => onMove(1)}
            >
              Back
            </Button>
          </Col>
          <Col xl={8} />
          <Col xl={8}>
            <Button
              htmlType="submit"
              className="default-primary-btn"
              type="primary"
              block
            >
              Save & Proceed
            </Button>
          </Col>
        </Row>
      </Form>
    </Skeleton>
  );
}

export default AdditionalProductInformationForm;
